import {
    ComponentProps,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react'

import { GetReportResponse, useFiltersStore } from '@/hooks'
import { ReportEditHeader } from './ReportEditHeader'
import { BillingFiltersContainer } from '@/components/data/BillingFilters/BillingFilters'
import { PayablesSummaryTableContainer } from '@/features/BillingPayments/containers/PayablesSummaryTableContainer/PayablesSummaryTableContainer'
import { mapParamsToFilters } from './mapParamsToFilters'
import useNavigationGuardStore from '@/stores/useNavigationGuardStore/useNavigationGuardStore'
import { mapFiltersToParams } from './mapFiltersToParams'
import {
    hasNameChanged,
    haveFiltersChanged,
} from '../../utils/detectReportChange'
import { processPayablesFilters } from '../../utils/processPayablesFilters'
import { DEFAULT_BILLING_FILTERING } from '@/utils/constants'
import { TableLoadingOverlay } from '@/components'
import { useTranslation } from 'react-i18next'

type ReportEditPayablesSummaryProps = {
    data: GetReportResponse
}

type OnChangeFn = ComponentProps<typeof BillingFiltersContainer>['onChange']

export const ReportEditPayablesSummary: React.FC<
    ReportEditPayablesSummaryProps
> = (props) => {
    const { t } = useTranslation('translations')

    const [data, setData] = useState(props.data)
    const [filters, setFilters] = useState(data.filters)
    const [name, setName] = useState(data.name)

    const rootNode = useFiltersStore((state) => state.rootNode)

    const parsedFilters = mapParamsToFilters(filters)
    const payablesSummaryRequestParams = useMemo(() => {
        return { ...processPayablesFilters(filters) }
    }, [filters])

    const baselineFilters = useMemo(() => {
        const params = mapFiltersToParams({
            ...DEFAULT_BILLING_FILTERING,
            node: rootNode?.nodeId !== undefined ? [rootNode.nodeId] : [],
        })

        const { commodityTypeIds: _commodityTypeIds, ...filtered } = params
        return filtered
    }, [rootNode?.nodeId])

    const handleResetFilters = useCallback(() => {
        setFilters(baselineFilters)
    }, [baselineFilters])
    const showResetFiltersButton = useMemo(() => {
        return haveFiltersChanged(filters, baselineFilters)
    }, [filters, baselineFilters])

    const onFiltersChange = useCallback<OnChangeFn>((filter, value) => {
        setFilters((prevParams) => {
            const prevFilters = mapParamsToFilters(prevParams)

            const newFilters = {
                ...prevFilters,
                [filter]: value,
            }
            const newParams = mapFiltersToParams(newFilters)

            return newParams
        })
    }, [])

    const updatedReport = {
        ...data,
        name,
        filters,
    }

    const setUnsavedChanges = useNavigationGuardStore(
        (state) => state.setUnsavedChanges
    )

    const handleReportSaved = () => {
        setFilters(updatedReport.filters)
        setName(updatedReport.name)
        setData(updatedReport)
        setUnsavedChanges(false)
    }

    useEffect(() => {
        const filtersChanged = haveFiltersChanged(filters, data.filters)
        const nameChanged = hasNameChanged(name, data.name)

        setUnsavedChanges(filtersChanged || nameChanged)
    }, [filters, name, data.filters, data.name, setUnsavedChanges])

    const [isUpdatePending, setIsUpdatePending] = useState(false)

    return (
        <div className="relative grid h-full grid-cols-[100%] grid-rows-[auto_1fr]">
            <div className="relative z-30 space-y-6">
                <ReportEditHeader
                    initialReportName={data.name}
                    report={updatedReport}
                    onChangeName={(reportName: string) => {
                        setName(reportName)
                    }}
                    onSaveAsNewReportSuccess={handleReportSaved}
                    onUpdatePendingChange={setIsUpdatePending}
                />
                <div className="px-6">
                    <BillingFiltersContainer
                        activeFilters={parsedFilters}
                        onChange={onFiltersChange}
                        handleReset={handleResetFilters}
                        showResetFiltersButton={showResetFiltersButton}
                    />
                </div>
            </div>

            <TableLoadingOverlay
                show={isUpdatePending}
                title={t('reporting.update_report.loading_save')}
                coverContainer
            />
            <div className="h-full overflow-hidden p-6">
                <PayablesSummaryTableContainer
                    filters={payablesSummaryRequestParams}
                />
            </div>
        </div>
    )
}
